'use client';

import { AssistantRuntimeProvider } from '@assistant-ui/react';
import { useChatRuntime } from '@assistant-ui/react-ai-sdk';
import { type ChatTransport, type UIMessage, type UIMessageChunk } from 'ai';
import Image from 'next/image';
import { type JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Icon, Text } from '@videoblocks/design-system';

import { FeedbackProvider } from '../../_context/FeedbackContext';
import { ChatSessionProvider } from '../../_context/SessionContext';
import { ChatPane } from '../chat/ChatPane';
import { ChatThread } from '../chat/ChatThread';
import { ResultsPane } from '../results/ResultsPane';
import { DemoChat } from './DemoChat';
import { SignUpModal, SIGNUP_EMAIL_INPUT_ID } from './SignUpModal';

const LAYOUT_CLASS = 'flex h-[calc(100dvh-7.75rem)] lg:h-[calc(100dvh-4rem)]';

// Arbitrary placeholder values for the blurred results header preview
const DEMO_RESULTS_TITLE = 'New search';
const DEMO_RESULTS_COUNT = '12 results found';

/**
 * A no-op transport that intercepts message submission, captures the user's
 * query text, and calls onSubmit instead of hitting the API.
 */
function useVisitorTransport(onSubmit: (query: string) => void): ChatTransport<UIMessage> {
    const onSubmitRef = useRef(onSubmit);

    useEffect(() => {
        onSubmitRef.current = onSubmit;
    }, [onSubmit]);

    return useMemo(
        () => ({
            sendMessages: ({ messages }: { messages: UIMessage[] }): Promise<ReadableStream<UIMessageChunk>> => {
                const lastUserMessage = messages.findLast((m) => m.role === 'user');
                const textPart = lastUserMessage?.parts?.find((p) => p.type === 'text');
                const queryText = textPart && 'text' in textPart ? textPart.text : '';

                if (queryText) {
                    onSubmitRef.current(queryText);
                }

                // Return an empty stream — the UI will transition before it matters
                return Promise.resolve(
                    new ReadableStream({
                        start(controller): void {
                            controller.close();
                        },
                    }),
                );
            },
            reconnectToStream: (): Promise<null> => Promise.resolve(null),
        }),
        [],
    );
}

/**
 * Visitor demo view for the conv-search page.
 * Initially shows the real chat UI (welcome + composer) so visitors can type a query.
 * After submitting, renders a blurred demo with their query shown unblurred,
 * plus a sign-up modal overlay.
 */
interface VisitorDemoProps {
    supportsAudio?: boolean;
    showTopResults?: boolean;
}

export function VisitorDemo({ supportsAudio = false, showTopResults = false }: VisitorDemoProps): JSX.Element {
    const [query, setQuery] = useState<string | null>(null);
    const [isDemoImageLoaded, setIsDemoImageLoaded] = useState(false);
    const handleSubmit = useCallback((q: string): void => setQuery(q), []);
    const focusEmailInput = useCallback((): void => {
        document.getElementById(SIGNUP_EMAIL_INPUT_ID)?.focus();
    }, []);
    const transport = useVisitorTransport(handleSubmit);
    const runtime = useChatRuntime({ transport });

    // Before submitting: show the real chat UI with welcome + results pane
    if (!query) {
        return (
            <AssistantRuntimeProvider runtime={runtime}>
                <ChatSessionProvider initialSessions={[]} supportsAudio={supportsAudio} showTopResults={showTopResults}>
                    {/* Visitors are never authenticated, so feedback is disabled. The
                        provider still has to be present because ChatThread renders
                        FeedbackWidget, which calls useFeedbackContext unconditionally. */}
                    <FeedbackProvider initialFeedback={null} lookupSucceeded enabled={false} userMessageThreshold={1}>
                        <div className={LAYOUT_CLASS}>
                            <ChatPane>
                                <ChatThread />
                            </ChatPane>
                            <div className="flex-1 bg-gray-50 overflow-hidden">
                                <ResultsPane />
                            </div>
                        </div>
                    </FeedbackProvider>
                </ChatSessionProvider>
            </AssistantRuntimeProvider>
        );
    }

    // After submitting: show blurred demo with their query + sign-up modal
    return (
        <div className={LAYOUT_CLASS}>
            <DemoChat query={query} onUpgradeClick={focusEmailInput} />
            <div className="relative flex-1 bg-gray-50 overflow-hidden">
                <div className="h-full blur-sm pointer-events-none select-none" aria-hidden="true">
                    {/* Static placeholder header — mirrors SessionHistoryDropdown layout */}
                    <div className="relative bg-white">
                        <div className="flex items-center px-6 py-2">
                            <div className="text-left">
                                <Text fontSize="base" fontWeight="semibold" className="leading-tight">
                                    {DEMO_RESULTS_TITLE}
                                </Text>
                                <Text fontSize="sm" fontWeight="regular" className="text-gray-500">
                                    {DEMO_RESULTS_COUNT}
                                </Text>
                            </div>
                            <Icon name="chevronDown" className="ml-6 w-3 h-3 text-black" />
                        </div>
                    </div>
                    <Image
                        src="/assets/images/conv-search/demo-results.png"
                        alt=""
                        width={0}
                        height={0}
                        sizes="100vw"
                        className={`w-full h-auto transition-opacity duration-1000 ${isDemoImageLoaded ? 'opacity-100' : 'opacity-0'}`}
                        onLoad={(): void => setIsDemoImageLoaded(true)}
                        aria-hidden="true"
                    />
                </div>
                <SignUpModal initialPrompt={query} />
            </div>
        </div>
    );
}
